import type {
  ContextMaterialIndexingStatus,
  KnowledgeDocumentIndexingUpdatedEvent,
  KnowledgeDocumentListItem,
} from '@digital-twins/shared-types'

export type ApplyKnowledgeIndexingEventResult = {
  documents: Array<KnowledgeDocumentListItem>
  /** True when the event names a Hub document not yet in the list. */
  missingDocument: boolean
}

/**
 * Patch a Hub document list row from a socket indexing event.
 * Returns the same array reference when nothing matched (avoids re-renders).
 * Callers should quiet-refetch when `missingDocument` is true so new uploads
 * appear after the durable first step without waiting for a full page remount.
 */
export function applyKnowledgeIndexingEventToDocuments(
  documents: Array<KnowledgeDocumentListItem>,
  event: KnowledgeDocumentIndexingUpdatedEvent,
): ApplyKnowledgeIndexingEventResult {
  if (!event.documentToken) {
    return { documents, missingDocument: false }
  }
  let changed = false
  let matched = false
  const next = documents.map((doc) => {
    if (doc.token !== event.documentToken) {
      return doc
    }
    matched = true
    changed = true
    return {
      ...doc,
      indexingStatus: event.indexingStatus as ContextMaterialIndexingStatus,
      indexingError: event.indexingError,
      ...(event.coverImageUrl
        ? { coverImageUrl: event.coverImageUrl }
        : {}),
      ...(event.title !== undefined ? { title: event.title } : {}),
      ...(event.summary !== undefined ? { summary: event.summary } : {}),
      ...(event.excerpt !== undefined ? { excerpt: event.excerpt } : {}),
    }
  })
  if (!matched) {
    return { documents, missingDocument: true }
  }
  return {
    documents: changed ? next : documents,
    missingDocument: false,
  }
}
